/**
 * AI Response Panel
 * Displays pre-computed AI draft responses for a contact.
 * Allows using, rejecting, and regenerating drafts.
 */

import { useState, useEffect } from 'react';
import {
  getContactDrafts,
  generateContactDrafts,
  markDraftUsed,
  rejectDraft,
} from '@/lib/api/endpoints/crm-engine';

interface AIDraft {
  id: string;
  contactId: string;
  actionType: string;
  subject: string;
  body: string;
  reasoning?: string;
  snippetsUsed: string[];
  confidence: number;
  tone: string;
  status: string;
  generatedAt: string;
}

interface AIResponsePanelProps {
  contactId: string;
  contactName: string;
  onUseDraft: (subject: string, body: string) => void;
}

export function AIResponsePanel({ contactId, contactName, onUseDraft }: AIResponsePanelProps) {
  const [drafts, setDrafts] = useState<AIDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [expandedReasoning, setExpandedReasoning] = useState<string | null>(null);

  useEffect(() => {
    loadDrafts();
  }, [contactId]);

  async function loadDrafts() {
    setLoading(true);
    try {
      const data = await getContactDrafts(contactId);
      setDrafts(data.drafts || []);
    } catch (err) {
      console.error('Failed to load drafts:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleGenerate() {
    setGenerating(true);
    try {
      await generateContactDrafts(contactId);
      await loadDrafts();
    } catch (err) {
      console.error('Failed to generate drafts:', err);
    } finally {
      setGenerating(false);
    }
  }

  async function handleUse(draft: AIDraft) {
    try {
      await markDraftUsed(draft.id);
    } catch (err) {
      console.error('Failed to mark draft used:', err);
    }
    onUseDraft(draft.subject, draft.body);
  }

  async function handleReject(draftId: string) {
    try {
      await rejectDraft(draftId);
      setDrafts(d => d.filter(x => x.id !== draftId));
    } catch (err) {
      console.error('Failed to reject draft:', err);
    }
  }

  const confidenceLabel = (c: number) => c >= 0.7 ? 'High' : c >= 0.4 ? 'Medium' : 'Low';
  const confidenceColor = (c: number) => c >= 0.7 ? '#16a34a' : c >= 0.4 ? '#d97706' : '#dc2626';

  const freshDrafts = drafts.filter(d => d.status === 'fresh');
  const otherDrafts = drafts.filter(d => d.status !== 'fresh');

  return (
    <div style={{ padding: '12px 0' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-muted)' }}>AI Drafts for {contactName}</span>
        <button
          onClick={handleGenerate}
          disabled={generating}
          style={{
            padding: '4px 12px', fontSize: 12, borderRadius: 6,
            border: '1px solid #ddd', background: generating ? '#f0f0f0' : 'white',
            cursor: generating ? 'default' : 'pointer', color: 'var(--color-text-muted)',
          }}
        >
          {generating ? 'Generating...' : 'Generate Now'}
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 16, textAlign: 'center', color: 'var(--color-text-faint)', fontSize: 13 }}>Loading drafts...</div>
      ) : freshDrafts.length === 0 && otherDrafts.length === 0 ? (
        <div style={{ padding: 16, textAlign: 'center', color: 'var(--color-text-faint)', fontSize: 13 }}>
          No pre-computed drafts yet. Click "Generate Now" to create one.
        </div>
      ) : (
        <div>
          {freshDrafts.map(draft => (
            <DraftCard
              key={draft.id}
              draft={draft}
              expanded={expandedReasoning === draft.id}
              onToggleReasoning={() => setExpandedReasoning(expandedReasoning === draft.id ? null : draft.id)}
              onUse={() => handleUse(draft)}
              onReject={() => handleReject(draft.id)}
              onRegenerate={handleGenerate}
              confidenceLabel={confidenceLabel}
              confidenceColor={confidenceColor}
            />
          ))}
          {otherDrafts.length > 0 && freshDrafts.length > 0 && (
            <div style={{ fontSize: 11, color: 'var(--color-text-faint)', marginTop: 8, marginBottom: 4 }}>Previous drafts</div>
          )}
          {otherDrafts.map(draft => (
            <DraftCard
              key={draft.id}
              draft={draft}
              expanded={expandedReasoning === draft.id}
              onToggleReasoning={() => setExpandedReasoning(expandedReasoning === draft.id ? null : draft.id)}
              onUse={() => handleUse(draft)}
              onReject={() => handleReject(draft.id)}
              onRegenerate={handleGenerate}
              confidenceLabel={confidenceLabel}
              confidenceColor={confidenceColor}
              dimmed
            />
          ))}
        </div>
      )}
    </div>
  );
}

function DraftCard({
  draft, expanded, onToggleReasoning, onUse, onReject, onRegenerate,
  confidenceLabel, confidenceColor, dimmed = false,
}: {
  draft: AIDraft;
  expanded: boolean;
  onToggleReasoning: () => void;
  onUse: () => void;
  onReject: () => void;
  onRegenerate: () => void;
  confidenceLabel: (c: number) => string;
  confidenceColor: (c: number) => string;
  dimmed?: boolean;
}) {
  return (
    <div style={{
      border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px',
      marginBottom: 8, opacity: dimmed ? 0.6 : 1,
      background: dimmed ? '#fafafa' : 'white',
    }}>
      {/* Subject + Confidence */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>{draft.subject}</span>
        <span style={{
          fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 10,
          background: `${confidenceColor(draft.confidence)}15`,
          color: confidenceColor(draft.confidence),
        }}>
          {confidenceLabel(draft.confidence)} ({Math.round(draft.confidence * 100)}%)
        </span>
      </div>

      {/* Body */}
      <div style={{ fontSize: 12, color: 'var(--color-text-muted)', lineHeight: 1.5, marginBottom: 8, whiteSpace: 'pre-wrap' }}>
        {draft.body}
      </div>

      {/* Snippets used */}
      {draft.snippetsUsed.length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
          <span style={{ fontSize: 10, color: 'var(--color-text-faint)' }}>Snippets:</span>
          {draft.snippetsUsed.map((s, i) => (
            <span key={i} style={{ fontSize: 10, background: 'var(--xavior-blue-bg, #e8f0fe)', color: 'var(--color-brand)', padding: '1px 6px', borderRadius: 4 }}>
              /{s}
            </span>
          ))}
        </div>
      )}

      {/* Reasoning toggle */}
      {draft.reasoning && (
        <div style={{ marginBottom: 6 }}>
          <button
            onClick={onToggleReasoning}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--color-text-faint)', padding: 0 }}
          >
            {expanded ? '▾ Hide reasoning' : '▸ Why this draft?'}
          </button>
          {expanded && (
            <div style={{ fontSize: 11, color: 'var(--color-text-faint)', marginTop: 4, padding: '6px 10px', background: '#f9fafb', borderRadius: 6, lineHeight: 1.4 }}>
              {draft.reasoning}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={onUse} style={{
          padding: '4px 12px', fontSize: 11, borderRadius: 6, border: 'none',
          background: 'var(--color-brand)', color: 'white', cursor: 'pointer',
        }}>
          Use Draft
        </button>
        <button onClick={onRegenerate} style={{
          padding: '4px 12px', fontSize: 11, borderRadius: 6,
          border: '1px solid #ddd', background: 'var(--color-surface)', cursor: 'pointer', color: 'var(--color-text-muted)',
        }}>
          Regenerate
        </button>
        <button onClick={onReject} style={{
          padding: '4px 12px', fontSize: 11, borderRadius: 6,
          border: '1px solid #fca5a5', background: 'var(--color-surface)', cursor: 'pointer', color: '#dc2626',
        }}>
          Reject
        </button>
      </div>
    </div>
  );
}
